import { CircularProgress, Container, Box, Typography } from '@mui/material';
import CalendarItemSelector from '@/components/Calendar/CalendarItemSelector';
import CalendarSelector from '@/components/Calendar/CalendarSelector';
import DaySheet from '@/components/Calendar/DaySheet';
import MonthCalendar from '@/components/Calendar/MonthCalendar';
import { useCalendarItems } from '@/hooks/useCalendarItems';
import { useCalendars } from '@/hooks/useCalendars';
import { useItemAvailability } from '@/hooks/useItemAvailability';
import { useToastError } from '@/hooks/useToastError';
import React, { useState } from 'react';
import VarausModal from '@/components/VarausModal';

const Kalenteri = () => {
  const [calendarId, setCalendarId] = useState(null);
  const [tuoteId, setTuoteId] = useState(null);
  const [tuoteNimi, setTuoteNimi] = useState(null);
  const today = new Date();

  const [aika, setAika] = useState({
    year: today.getFullYear(),
    month: today.getMonth(),
  });

  const [selectedDay, setSelectedDay] = useState(null);
  const [varausModalOpen, setVarausModalOpen] = useState(false);

  const { data: calendars, loading: loadingCalendars, error: errorCalendars } = useCalendars();
  const { data: calendarItems, loading: loadingCalendarItems, error: errorCalendarItems } = useCalendarItems(calendarId);
  const { data: availability, loading: loadingAvailability, error: errorAvailability, refetch: refetchAvailability } = useItemAvailability(tuoteId, aika.month, aika.year);

  useToastError(errorCalendars, 'Kalenterien hakeminen epäonnistui!');
  useToastError(errorCalendarItems, 'Kalenterin tuotteiden hakeminen epäonnistui!');
  useToastError(errorAvailability, errorAvailability?.message || 'Tuotteen saatavuuden hakeminen epäonnistui!');

  const goPrevMonth = () => {
    setAika((v) => {
      const month = v.month - 1;
      return month < 0
        ? { year: v.year - 1, month: 11 }
        : { year: v.year, month };
    });
  };

  const goNextMonth = () => {
    setAika((v) => {
      const month = v.month + 1;
      return month > 11
        ? { year: v.year + 1, month: 0 }
        : { year: v.year, month };
    });
  };

  const handleCalendarChange = (newId) => {
    setCalendarId(newId);
    setTuoteId(null);
  };

  const handleTuoteChange = (newId, newName) => {
    setTuoteId(newId);
    setTuoteNimi(newName);
    setAika({ year: today.getFullYear(), month: today.getMonth() });
  };

  const handleReservationCreated = async () => {
    // Refetch the calendar month data
    await refetchAvailability();
    // Close the DaySheet
    setSelectedDay(null);
  };

  return (
    <Container disableGutters sx={{ marginTop: 1, backgroundColor: 'white' }}>
      {
        loadingCalendars
          ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                <CircularProgress />
              </Box>
            )
          : (
              <CalendarSelector calendars={calendars} value={calendarId} onChange={handleCalendarChange} />
            )
      }

      {
        calendarId && (
          loadingCalendarItems
            ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                  <CircularProgress />
                </Box>
              )
            : <CalendarItemSelector items={calendarItems} value={tuoteId} onChange={handleTuoteChange} />
        )
      }

      {
        tuoteId && (
          loadingAvailability
            ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 1 }}>
                  <CircularProgress />
                </Box>
              )
            : (!errorAvailability)
                ? (
                    <MonthCalendar
                      year={aika.year}
                      month={aika.month}
                      availabilityData={availability}
                      onDayClick={(date, meta) => {
                        setSelectedDay({ date, reservations: meta?.reservationIds || [], available: !!meta?.available });
                      }}
                      onPrevMonth={goPrevMonth}
                      onNextMonth={goNextMonth}
                    />
                  )
                : (
                    <Box sx={{ padding: 2 }}>
                      <Typography color="error">
                        {errorAvailability.message}
                      </Typography>
                    </Box>
                  )
        )
      }

      <DaySheet
        open={Boolean(selectedDay)}
        onOpenVarausModal={() => setVarausModalOpen(true)}
        onClose={() => setSelectedDay(null)}
        day={selectedDay}
      />

      <VarausModal
        open={varausModalOpen}
        onClose={() => setVarausModalOpen(false)}
        productId={tuoteId}
        productName={tuoteNimi}
        selectedDate={selectedDay}
        onReservationCreated={handleReservationCreated}
      />
    </Container>
  );
};

export default Kalenteri;
